import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import { expenseApiService } from "services/expenseApiService";
import DeleteModal from "pages/Calendar/DeleteModal";
import UpdateExpenseModal from "./UpdateExpenseModal";

const AllExpenses = () => {
  const navigate = useNavigate();

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [deletingExpense, setDeletingExpense] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  // Fetch expenses
  const fetchExpenses = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await expenseApiService.getAllExpenses();
      setExpenses(data);
    } catch (error) {
      // toast.error("Failed to fetch expenses");
      console.error("Error fetching expenses:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const showDeleteModal = (id) => {
    setSelectedExpenseId(id);
    setDeleteModal(true);
  };

  const handleDeleteExpense = async () => {
    setDeletingExpense(true);
    try {
      await expenseApiService.deleteExpense(selectedExpenseId);
      toast.success("Expense deleted successfully");
      setDeleteModal(false);
      fetchExpenses(false);
    } catch (error) {
      toast.error("Failed to delete expense");
      console.error("Error deleting expense:", error);
    } finally {
      setDeletingExpense(false);
    }
  };

  const handleUpdateExpense = (expense) => {
    setSelectedExpense(expense);
    setUpdateModalOpen(true);
  };

  const handleUpdateSuccess = () => {
    fetchExpenses(false);
    toast.success("Expense updated successfully");
  };

  // Filter expenses based on search and date
  const filteredExpenses = useMemo(() => {
    let filtered = expenses;

    // Apply date filter
    if (dateFilter) {
      filtered = filtered.filter(expense => expense.date === dateFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (expense) =>
          (expense.store_name && expense.store_name.toLowerCase().includes(query)) ||
          (expense.paid_by && expense.paid_by.toLowerCase().includes(query)) ||
          (expense.note && expense.note.toLowerCase().includes(query)) ||
          (expense.goods_with && expense.goods_with.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [expenses, dateFilter, searchQuery]);

  const formatCurrency = (amount) => {
    if (!amount) return "$0";
    return `$${parseFloat(amount).toLocaleString('en-US')}`;
  };

  const formatItems = (items) => {
    if (!items || !items.length) return "-";

    return (
      <div>
        {items.map((item, index) => (
          <div key={index} className="mb-1">

            {item.name} x {item.quantity} - {formatCurrency(item.price)}

          </div>
        ))}
      </div>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: "Date",
        accessorKey: "date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">{row.original.date || "-"}</div>
          </div>
        ),
      },
      {
        header: "Day",
        accessorKey: "day",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">{row.original.day || "-"}</div>
          </div>
        ),
      },
      {
        header: "Store",
        accessorKey: "store_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {row.original.store_name || "-"}
          </div>
        ),
      },
      // {
      //   header: "Paid By",
      //   accessorKey: "paid_by",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => (
      //     <div className="">
      //       {row.original.paid_by || "-"}
      //     </div>
      //   ),
      // },
      {
        header: "Items",
        accessorKey: "items",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => formatItems(row.original.items),
      },
      {
        header: "Total",
        accessorKey: "total",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="text-success">
            {formatCurrency(row.original.total)}
          </div>
        ),
      },
      {
        header: "Note",
        accessorKey: "note",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <small className="d-block text-truncate" style={{ maxWidth: "150px" }}>
              {row.original.note || "-"}
            </small>
          </div>
        ),
      },
      {
        header: "Goods With",
        accessorKey: "goods_with",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <small className="d-block text-truncate" style={{ maxWidth: "150px" }}>
              {row.original.goods_with || "-"}
            </small>
          </div>
        ),
      },
      {
        header: "Receipt",
        accessorKey: "receipt",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-center">
            {row.original.receipt ? (
              <button
                onClick={() => setSelectedImage(row.original.receipt)}
                className="btn btn-info btn-sm"
                title="View Receipt"
              >
                <i className="fas fa-eye"></i>
              </button>
            ) : (
              <span className="text-muted">-</span>
            )}
          </div>
        ),
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              onClick={() => handleUpdateExpense(row.original)}
              className="btn btn-warning btn-sm"
              title="Edit Expense"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => showDeleteModal(row.original.id)}
              title="Delete Expense"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // Calculate statistics
  const totalExpenses = expenses.length;
  const totalAmount = expenses.reduce((sum, expense) => sum + parseFloat(expense.total || 0), 0);
  const today = new Date().toISOString().split('T')[0];
  const todayExpenses = expenses.filter(expense => expense.date === today).length;
  const todayAmount = expenses
    .filter(expense => expense.date === today)
    .reduce((sum, expense) => sum + parseFloat(expense.total || 0), 0);

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Expenses" breadcrumbItem="All Expenses" />

        {/* Statistics Cards */}
        {/* <div className="row mb-4">
          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Total Expenses</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{totalExpenses}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary rounded-circle">
                        <i className="fas fa-file-invoice-dollar font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Total Amount</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{formatCurrency(totalAmount)}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-success rounded-circle">
                        <i className="fas fa-dollar-sign font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Today's Expenses</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{todayExpenses}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-info rounded-circle">
                        <i className="fas fa-calendar-day font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Today's Amount</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{formatCurrency(todayAmount)}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-warning rounded-circle">
                        <i className="fas fa-money-bill-wave font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Filters and Actions */}
        <div className="row mb-3">
          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3 align-items-center">


                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by store, paid by, note..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: "300px" }}
                  />
                </div>

                <div className="d-flex align-items-center gap-2">
                  {/* <label className="form-label mb-0">Date:</label> */}
                  <input
                    type="date"
                    className="form-control"
                    style={{ width: "auto" }}
                    value={dateFilter}
                    onChange={e => setDateFilter(e.target.value)}
                  />
                </div>


                {(dateFilter || searchQuery) && (
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setDateFilter("");
                      setSearchQuery("");
                    }}
                  >
                    <i className="fas fa-times me-1"></i> Clear Filters
                  </button>
                )}
              </div>

              <div className="d-flex gap-2">
                <button
                  onClick={() => navigate("/create-expense")}
                  className="btn btn-success"
                >
                  <i className="fas fa-plus me-1"></i> Create New Expense
                </button>

                <button
                  className="btn btn-light"
                  onClick={() => fetchExpenses(false)}
                  disabled={refreshing}
                  title="Refresh Expenses"
                >
                  <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Expenses Table */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* <div className="mb-3">
                  <h5 className="card-title mb-0">
                    Showing {filteredExpenses.length} of {totalExpenses} expenses
                  </h5>
                </div> */}

                <TableContainer
                  columns={columns}
                  data={filteredExpenses}
                  isGlobalFilter={false}
                  isPagination={true}
                  pagination="pagination"
                  paginationWrapper="dataTables_paginate paging_simple_numbers"
                  tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                  isCustomPageSize={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteExpense}
          loader={deletingExpense}
          onCloseClick={() => setDeleteModal(false)}
          title="Delete Expense"
          message="Are you sure you want to delete this expense? This action cannot be undone."
        />

        {selectedExpense && (
          <UpdateExpenseModal
            isOpen={updateModalOpen}
            toggle={() => setUpdateModalOpen(false)}
            expense={selectedExpense}
            onUpdateSuccess={handleUpdateSuccess}
          />
        )}

        {/* Image Viewer Modal */}
        {selectedImage && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.9)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content bg-transparent border-0">
                <div className="modal-header border-0">
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setSelectedImage(null)}
                  ></button>
                </div>
                <div className="modal-body text-center">
                  <img
                    src={selectedImage}
                    alt="Receipt"
                    className="img-fluid"
                    style={{ maxHeight: '80vh' }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllExpenses;